import React, { useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { queryClient } from '@/lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';
import { Toaster } from '@/components/ui/toaster';
import NotFound from '@/pages/not-found';
import Home from '@/pages/Home';
import AuthCallback from '@/pages/AuthCallback';
import TermsAndConditions from '@/pages/TermsAndConditions';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import DataDeletion from '@/pages/DataDeletion';
import SubscriptionPlans from '@/pages/SubscriptionPlans';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMinimumWidth } from '@/hooks/useMinimumWidth';
import { MobileNotSupportedOverlay } from '@/components/overlays/MobileNotSupportedOverlay.tsx';
import MinimumWidthOverlay from '@/components/overlays/MinimumWidthOverlay.tsx';

// Protected route that requires authentication
function ProtectedRoute({ component: Component }: { component: React.ComponentType<unknown> }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [ , navigate ] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('Protected route accessed without authentication, redirecting to home');
      navigate('/', { replace: true });
    }
  }, [ isLoading, isAuthenticated, navigate ]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <Spinner
          size="large"
          variant="primary"
          text="Loading..."
        />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Component/>;
  }

  return null;
}

// Protected route that requires authentication

function Router() {
  return (
    <Switch>
      <Route path="/terms" component={TermsAndConditions}/>
      <Route path="/privacy" component={PrivacyPolicy}/>
      <Route path="/data-deletion" component={DataDeletion}/>
      <Route path="/plans">
        <ProtectedRoute component={SubscriptionPlans}/>
      </Route>
      <Route path="/auth/callback" component={AuthCallback}/>
      {/* Home handles both landing page (unauthenticated) and video chat (authenticated) */}
      <Route path="/" component={Home}/>
      <Route component={NotFound}/>
    </Switch>
  );
}

// Fingerprint.com public API key (safe to expose in frontend)
const fingerprintPublicKey = import.meta.env.VITE_FINGERPRINT_PUBLIC_KEY;

function AppWithOverlays() {
  const isMobile = useIsMobile();
  const { isWidthSufficient } = useMinimumWidth();
  const { isAuthenticated } = useAuth();

  return (
    <>
      <Router/>
      <Toaster/>
      {/* Show minimum width overlay when screen is too narrow (only for authenticated users in video chat) */}
      {isAuthenticated && !isWidthSufficient && <MinimumWidthOverlay/>}
      {/* Show mobile not supported overlay on mobile devices (only for authenticated users) */}
      <MobileNotSupportedOverlay isVisible={isAuthenticated && isMobile}/>
    </>
  );
}

function App() {
  // Only wrap with FpjsProvider if API key is configured
  const AppContent = (
    <AuthProvider>
      <AppWithOverlays/>
    </AuthProvider>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {fingerprintPublicKey ? (
        <FpjsProvider
          loadOptions={{
            apiKey: fingerprintPublicKey,
            scriptUrlPattern: [
              // Using Cloudflare Worker custom domain (info.blendedchat.com) since main domain uses DNS-only mode
              // Paths: WORKER_PATH/AGENT_SCRIPT_DOWNLOAD_PATH from Fingerprint Cloudflare integration
              'https://info.blendedchat.com/tX7Q0tVPVMYSPs3K/vHdmRKIbFV0Mqc1H?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>',
              'https://fpnpmcdn.net/v<version>/<apiKey>/loader_v<loaderVersion>.js', // Fallback
            ],
            endpoint: [
              // WORKER_PATH/GET_RESULT_PATH from Fingerprint Cloudflare integration
              'https://info.blendedchat.com/tX7Q0tVPVMYSPs3K/nHTetlcfYtu5zdXI',
              'https://api.fpjs.io', // Fallback
            ],
          }}
        >
          {AppContent}
        </FpjsProvider>
      ) : (
        AppContent
      )}
    </QueryClientProvider>
  );
}

export default App;
